import React, { useEffect } from 'react';
import { useNotification } from '../contexts/NotificationContext';

const NotificationModal = () => {
  const { notification, hideNotification, confirmDialog } = useNotification();

  useEffect(() => {
    if (notification && notification.duration > 0) {
      const timer = setTimeout(() => {
        hideNotification();
      }, notification.duration);
      return () => clearTimeout(timer);
    }
  }, [notification, hideNotification]);

  if (!notification && !confirmDialog) return null;

  // Notification Modal (auto-dismiss)
  if (notification) {
    const { type, title, message } = notification;

    const typeStyles = {
      success: {
        gradient: 'bg-gradient-to-r from-green-500 to-emerald-600',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        textColor: 'text-green-600 dark:text-green-400',
        buttonGradient: 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700',
        icon: (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        ),
      },
      error: {
        gradient: 'bg-gradient-to-r from-red-500 to-rose-600',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        textColor: 'text-red-600 dark:text-red-400',
        buttonGradient: 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700',
        icon: (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        ),
      },
      warning: {
        gradient: 'bg-gradient-to-r from-yellow-500 to-amber-600',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        textColor: 'text-yellow-600 dark:text-yellow-400',
        buttonGradient: 'bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-700 hover:to-amber-700',
        icon: (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        ),
      },
      info: {
        gradient: 'bg-gradient-to-r from-[#32B1FF] to-[#2A9FEB]',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        textColor: 'text-[#32B1FF]',
        buttonGradient: 'bg-gradient-to-r from-[#32B1FF] to-[#2A9FEB] hover:from-[#2A9FEB] hover:to-[#228ED8]',
        icon: (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        ),
      },
    };

    const style = typeStyles[type] || typeStyles.info;

    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none">
        <div 
          className="pointer-events-auto animate-fade-in-up"
          style={{ animation: 'fadeInUp 0.3s ease-out' }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl border dark:border-gray-800 w-full max-w-md mx-4 overflow-hidden">
            {/* Colored top stripe */}
            <div className={`h-2 ${style.gradient}`}></div>
            
            <div className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${style.bgColor} ${style.textColor}`}>
                  {style.icon}
                </div>
                
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
                    {message}
                  </p>
                </div>
              </div>
              
              <button
                onClick={hideNotification}
                className={`w-full px-4 py-2.5 rounded-lg font-medium text-white transition-all shadow-sm hover:shadow-md ${style.buttonGradient}`}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Confirmation Dialog
  if (confirmDialog) {
    const { title, message, confirmText, cancelText, confirmButtonClass, onConfirm, onCancel } = confirmDialog;

    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
          onClick={onCancel}
        ></div>
        
        {/* Modal */}
        <div 
          className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border dark:border-gray-800 w-full max-w-md mx-4 animate-scale-in"
          style={{ animation: 'scaleIn 0.2s ease-out' }}
        >
          <div className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-[#32B1FF]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
                  {message}
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={onCancel}
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium transition-colors"
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-white transition-all shadow-sm hover:shadow-md ${
                  confirmButtonClass || 'bg-gradient-to-r from-[#32B1FF] to-[#2A9FEB] hover:from-[#2A9FEB] hover:to-[#228ED8]'
                }`}
              >
                {confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Add keyframe animations to global styles
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;
document.head.appendChild(styleSheet);

export default NotificationModal;

